import axios, { AxiosResponse, AxiosError, InternalAxiosRequestConfig } from "axios";
import { AuthContextProps } from "react-oidc-context";
import { config } from "../contexts/UrlContext";
import { toast } from "react-toastify";

// Global reference to auth context - will be set from components
let globalAuth: AuthContextProps | undefined;
let isAuthEnabled = false; // Track if authentication is enabled
let interceptorsInitialized = false; // Track if interceptors are already set up

// Track if we're currently refreshing token to avoid multiple refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (error:any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach(({ resolve, reject }) => {
        if (error) {
            reject(error);
        } else {
            resolve(token!);
        }
    });

    failedQueue = [];
};

// Set global auth reference and enable/disable auth
export const setGlobalAuth = (auth: AuthContextProps | undefined) => {
    globalAuth = auth;
    isAuthEnabled = !!auth; // Enable auth only if auth context is provided

    // Initialize interceptors when auth is enabled
    if (isAuthEnabled && !interceptorsInitialized) {
        setupAxiosInterceptors();
    }
};

// Disable authentication for cases where no auth is used
export const disableAuth = () => {
    globalAuth = undefined;
    isAuthEnabled = false;

    // Set up basic interceptors without auth logic
    if (!interceptorsInitialized) {
        setupBasicInterceptors();
    }
};

// Check if token is expired or about to expire (within 5 minutes)
const isTokenExpired = (token: string): boolean => {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Math.floor(Date.now() / 1000);
        // Consider token expired if it expires within 5 minutes (300 seconds)
        return payload.exp < (currentTime + 300);
    } catch (error) {
        return true; // If we can't parse token, consider it expired
    }
};

// Refresh token using OIDC context
const refreshToken = async (): Promise<string> => {
    if (!globalAuth) {
        throw new Error('No authentication context available');
    }

    try {
        // Use signinSilent to refresh token
        const user = await globalAuth.signinSilent();
        if (user && user.access_token) {
            return user.access_token;
        }
        throw new Error('Failed to refresh token');
    } catch (error) {
        // If silent refresh fails, redirect to login
        console.error('Token refresh failed:', error);
        globalAuth.signinRedirect();
        throw error;
    }
};

// Setup interceptors with authentication logic
const setupAxiosInterceptors = () => {
    if (!axios) {
        console.error('Axios is not available');
        return;
    }

    interceptorsInitialized = true;

    // Request interceptor to add auth header and handle token refresh
    axios.interceptors.request.use(
        async (config: InternalAxiosRequestConfig) => {
            if (isAuthEnabled && globalAuth && globalAuth.user?.access_token) {
                // Check if token is expired
                if (isTokenExpired(globalAuth.user.access_token)) {
                    if (!isRefreshing) {
                        isRefreshing = true;
                        try {
                            const newToken = await refreshToken();
                            processQueue(null, newToken);
                            config.headers.Authorization = `Bearer ${newToken}`;
                        } catch (refreshError) {
                            processQueue(refreshError, null);
                            throw refreshError;
                        } finally {
                            isRefreshing = false;
                        }
                    } else {
                        // If already refreshing, queue this request
                        return new Promise((resolve, reject) => {
                            failedQueue.push({
                                resolve: (token: string) => {
                                    config.headers.Authorization = `Bearer ${token}`;
                                    resolve(config);
                                },
                                reject: (error) => {
                                    reject(error);
                                }
                            });
                        });
                    }
                } else {
                    config.headers.Authorization = `Bearer ${globalAuth.user.access_token}`;
                }
            }
            return config;
        },
        (error) => Promise.reject(error)
    );

    // Response interceptor to handle 401 errors
    axios.interceptors.response.use(
        (response) => response,
        async (error: AxiosError) => {
            const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

            if (isAuthEnabled && error.response?.status === 401 && !originalRequest._retry) {
                if (isRefreshing) {
                    return new Promise((resolve, reject) => {
                        failedQueue.push({
                            resolve: (token: string) => {
                                originalRequest.headers.Authorization = `Bearer ${token}`;
                                resolve(axios(originalRequest));
                            },
                            reject: (err: any) => {
                                reject(err);
                            }
                        });
                    });
                }

                originalRequest._retry = true;
                isRefreshing = true;

                try {
                    const newToken = await refreshToken();
                    processQueue(null, newToken);
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return axios(originalRequest);
                } catch (refreshError) {
                    processQueue(refreshError, null);
                    // If refresh fails, redirect to login
                    if (globalAuth) {
                        globalAuth.signinRedirect();
                    }
                    return Promise.reject(refreshError);
                } finally {
                    isRefreshing = false;
                }
            }

            return Promise.reject(error);
        }
    );
};

// Setup basic interceptors without authentication logic
const setupBasicInterceptors = () => {
    if (!axios) {
        console.error('Axios is not available');
        return;
    }

    interceptorsInitialized = true;

    // Basic request interceptor (no auth logic)
    axios.interceptors.request.use(
        (config: InternalAxiosRequestConfig) => {
            return config;
        },
        (error) => Promise.reject(error)
    );

    // Basic response interceptor (no auth logic)
    axios.interceptors.response.use(
        (response) => response,
        (error: AxiosError) => {
            return Promise.reject(error);
        }
    );
};

// Don't initialize interceptors immediately - wait for auth context to be set

export const getRequest = async (auth: AuthContextProps | undefined, endpoint: string): Promise<AxiosResponse> => {
    // Set global auth reference if provided
    if (auth) {
        setGlobalAuth(auth);
    }

    const url: string = endpoint[0] === '/' ? `${config.baseUrl}/api${endpoint}` : `${config.baseUrl}/api/${endpoint}`;

    try {
        const response = await axios.get(url);
        return response;
    } catch (error) {
        let err: any = error;
        // Only show auth-related errors if authentication is enabled
        if (isAuthEnabled && err.response?.status === 401) {
            toast.error("Session expired. Please log in again.");
        } else if (err.response?.status >= 500) {
            toast.error("Server error occurred. Please try again later.");
        }
        throw error;
    }
}

export const getRequestResponseType = async (auth: AuthContextProps | undefined, endpoint: string, responseType: any): Promise<AxiosResponse> => {
    // Set global auth reference if provided
    if (auth) {
        setGlobalAuth(auth);
    }

    const url: string = endpoint[0] === '/' ? `${config.baseUrl}/api${endpoint}` : `${config.baseUrl}/api/${endpoint}`;

    try {
        const response = await axios.get(url, { responseType: responseType });
        return response;
    } catch (error) {
        let err = error as any;
        // Only show auth-related errors if authentication is enabled
        if (isAuthEnabled && err.response?.status === 401) {
            toast.error("Session expired. Please log in again.");
        } else if (err.response?.status >= 500) {
            toast.error("Server error occurred. Please try again later.");
        }
        throw error;
    }
}

export const postRequest = async (auth: AuthContextProps | undefined, endpoint: string, data: any): Promise<AxiosResponse> => {
    // Set global auth reference if provided
    if (auth) {
        setGlobalAuth(auth);
    }

    const url: string = endpoint[0] === '/' ? `${config.baseUrl}/api${endpoint}` : `${config.baseUrl}/api/${endpoint}`;

    try {
        let response: AxiosResponse = await axios.post(url, data);
        
        return response;
    } catch (error) {
        let err = error as any;
        // Only show auth-related errors if authentication is enabled
        if (isAuthEnabled && err.response?.status === 401) {
            toast.error("Session expired. Please log in again.");
        } else if (err.response?.status >= 500) {
            toast.error("Server error occurred. Please try again later.");
        }
        throw error;
    }
}

